import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Loader2, FileSearch, ArrowLeft, RefreshCw, Save, ShieldCheck,
  ExternalLink, AlertTriangle, BookOpen, Microscope, Archive, Send,
  CheckCircle2, XCircle, HelpCircle, Pause, Sparkles, Wand2, Lock, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  adminGetDraft, adminPatchDraft, adminArchiveDraft, adminPublishDraft,
  adminDossierSetVisibility,
  adminDraftDecision, adminDraftFillGaps, adminDraftPolish, adminDraftLock,
} from "../lib/api";
import { HeroControl } from "../components/admin/HeroControl";

const OP_TOKEN_KEY = "archive-admin-token";

const CONFIDENCE_META = {
  strong_source_base: { label: "STRONG SOURCE BASE", color: "#5DD4B3" },
  mixed_source_base:  { label: "MIXED SOURCE BASE",  color: "#CBB892" },
  weak_source_base:   { label: "WEAK SOURCE BASE — NEEDS MANUAL VERIFICATION", color: "#D92D20" },
};
const DUPE_META = {
  novel:                 { label: "NOVEL TOPIC",         color: "#5DD4B3" },
  overlaps:              { label: "OVERLAPS EXISTING",   color: "#CBB892" },
  duplicate:             { label: "DUPLICATE",           color: "#D92D20" },
  subset_of:             { label: "SUBSET OF EXISTING",  color: "#CBB892" },
};

const BUCKET_LABEL = {
  primary: "Primary",
  official_documents: "Official documents",
  court_or_filings: "Court / filings",
  investigative_journalism: "Investigative journalism",
  books: "Books",
  academic: "Academic",
  low_confidence: "Low confidence",
};

const KIND_COLOR = {
  fact: "#5DD4B3",
  allegation: "#CBB892",
  disputed: "#E0A526",
  unsupported: "#D92D20",
};

const Tag = ({ children, color = "#A39E93", title }) => (
  <span
    title={title}
    style={{ color, borderColor: color }}
    className="inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em]"
  >
    {children}
  </span>
);

const SectionShell = ({ icon: Icon, title, count, children }) => (
  <section className="border border-[#2D2A26] bg-[#131313] p-5 mb-4">
    <div className="flex items-baseline justify-between mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
        {Icon && <Icon size={12} />} // {title}
      </div>
      {count !== undefined && <div className="font-mono text-[10px] text-[#757065]">{count}</div>}
    </div>
    {children}
  </section>
);

const Field = ({ label, value, onChange, multiline = false, rows = 3, disabled }) => (
  <label className="block mb-3">
    <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">{label}</span>
    {multiline ? (
      <textarea
        value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} disabled={disabled}
        className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 text-[#E4DFD3] text-sm leading-relaxed disabled:opacity-50"
      />
    ) : (
      <input
        type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 text-[#E4DFD3] text-sm disabled:opacity-50"
      />
    )}
  </label>
);

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "claims",    label: "Claim map" },
  { key: "timeline",  label: "Timeline" },
  { key: "sources",   label: "Sources" },
  { key: "evidence",  label: "Evidence" },
  { key: "draft",     label: "Draft" },
  { key: "notes",     label: "Notes / audit" },
];

const AdminInvestigation = () => {
  const { draftId } = useParams();
  const nav = useNavigate();
  const { token: userToken } = useAuth();
  const opToken = (typeof window !== "undefined") ? sessionStorage.getItem(OP_TOKEN_KEY) : null;

  const [data, setData] = useState(null);   // { draft, submission }
  const [tab, setTab] = useState("overview");
  const [draft, setDraft] = useState(null); // working copy
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [notesText, setNotesText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminGetDraft(opToken, draftId, userToken);
      setData(r);
      setDraft(r.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load draft");
    } finally { setLoading(false); }
  }, [opToken, draftId, userToken]);

  useEffect(() => { load(); }, [load]);

  // Poll while running
  useEffect(() => {
    if (!data?.draft || data.draft.status !== "running") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data, load]);

  const isRunning = draft?.status === "running";
  const isPublished = draft?.status === "published";
  const isApproved = draft?.editorial_status === "approved";

  const pullFromPublic = async () => {
    if (!window.confirm("Pull this dossier from public? It disappears from every public surface immediately and returns to Review → Needs Review with all research intact.")) return;
    setPulling(true);
    try {
      await adminDossierSetVisibility(opToken, "topic", draft.published_topic_id,
        { status: "review", reason: "incomplete_dossier" }, userToken);
      toast.success("Pulled from public — returned to Needs Review");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed");
    } finally { setPulling(false); }
  };

  const setIn = (path, value) => {
    setDraft((d) => {
      const next = { ...(d || {}) };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...(obj[keys[i]] || {}) };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const patch = {
        framing: draft.framing, claim_map: draft.claim_map,
        timeline: draft.timeline, sources_flat: draft.sources_flat,
        evidence_matrix: draft.evidence_matrix, contradictions: draft.contradictions,
        draft: draft.draft, what_is_missing: draft.what_is_missing,
        suggested_slug: draft.suggested_slug, suggested_tags: draft.suggested_tags,
        confidence: draft.confidence,
        notes_append: notesText.trim() || undefined,
      };
      await adminPatchDraft(opToken, draftId, patch, userToken);
      setNotesText("");
      toast.success("Draft saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const markNeedsResearch = async () => {
    try {
      await adminPatchDraft(opToken, draftId, { needs_more_research: true }, userToken);
      toast.success("Marked as needs-more-research");
      load();
    } catch { toast.error("Update failed"); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this investigation without publishing?")) return;
    try {
      await adminArchiveDraft(opToken, draftId, userToken);
      toast.success("Archived");
      nav("/admin/review");
    } catch { toast.error("Archive failed"); }
  };

  const decide = async (decision) => {
    const label = { approve: "Approve", deny: "Deny", undetermined: "Mark undetermined", hold: "Put on hold", needs_review: "Send back to Needs review" }[decision];
    if (!window.confirm(`${label} this case?`)) return;
    const note = window.prompt("Optional note for the audit log:", "") || "";
    try {
      const r = await adminDraftDecision(opToken, draftId, decision, note, userToken);
      toast.success(`Case marked: ${r.editorial_status.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    }
  };

  const fillGaps = async () => {
    if (!window.confirm("Run a fill-gaps + rewrite pass with AI? This will research the unresolved items and rewrite the draft body.")) return;
    try {
      await adminDraftFillGaps(opToken, draftId, userToken);
      toast.success("Fill-gaps pass started — refresh in ~60s.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fill-gaps failed"); }
  };

  const polish = async () => {
    if (!window.confirm("Run an editorial polish pass? No new facts will be added — pure rewrite for flow, clarity, and tone.")) return;
    try {
      await adminDraftPolish(opToken, draftId, userToken);
      toast.success("Polish pass started — refresh in ~30s.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Polish failed"); }
  };

  const toggleLock = async () => {
    const locked = !draft.ai_locked;
    try {
      await adminDraftLock(opToken, draftId, locked, userToken);
      toast.success(locked ? "Draft locked from AI" : "Draft unlocked");
      load();
    } catch { toast.error("Lock toggle failed"); }
  };

  const publish = async () => {
    if (!draft) return;
    if (!window.confirm("Publish this dossier to the public Archive? This action is recorded in the audit log.")) return;
    setPublishing(true);
    try {
      const framing = draft.framing || {};
      const body = draft.draft || {};
      const r = await adminPublishDraft(opToken, draftId, {
        title: (framing.title_suggestions || [])[0] || data?.submission?.title,
        slug: draft.suggested_slug,
        category: framing.category_suggestion,
        tagline: body.dek,
        tags: draft.suggested_tags,
      }, userToken);
      toast.success(`Published as /topic/${r.slug}`);
      nav(`/topic/${r.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  if (loading && !draft) return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 text-[#A39E93] flex items-center gap-2">
      <Loader2 className="animate-spin" size={14} /> Loading investigation…
    </div>
  );
  if (!draft) return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 text-[#A39E93]">Draft not found.</div>
  );

  const sub = data?.submission;
  const framing = draft.framing || {};
  const cm = draft.claim_map || {};
  const tl = draft.timeline || [];
  const flat = draft.sources_flat || [];
  const em = draft.evidence_matrix || [];
  const cd = draft.contradictions || [];
  const dc = draft.duplicate_check || {};
  const body = draft.draft || {};
  const wm = draft.what_is_missing || [];
  const conf = CONFIDENCE_META[draft.confidence] || CONFIDENCE_META.mixed_source_base;
  const dupe = DUPE_META[dc.verdict] || null;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="admin-investigation-page">
      <Link to="/admin/submissions" className="inline-flex items-center gap-2 text-[#757065] hover:text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.25em] mb-3">
        <ArrowLeft size={11} /> Back to Editorial Desk
      </Link>

      {/* Top summary */}
      <div className="border border-[#2D2A26] bg-[#131313] aged-paper p-6 mb-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D92D20]/60 to-transparent" />
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Tag color={conf.color} title="AI source-base confidence">{conf.label}</Tag>
          {dupe && <Tag color={dupe.color}>{dupe.label}</Tag>}
          {isRunning && <Tag color="#E0A526">// INVESTIGATING…</Tag>}
          {isPublished && <Tag color="#5DD4B3">// PUBLISHED</Tag>}
          {sub && <Tag>CAT: {sub.category}</Tag>}
          {sub && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">
              tracking: <code className="text-[#A39E93]">{sub.id}</code>
            </span>
          )}
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-[#E4DFD3] mb-2" data-testid="draft-headline">
          {body.headline || (framing.title_suggestions || [])[0] || sub?.title || "(untitled)"}
        </h1>
        {body.dek && <p className="text-[#A39E93] text-base mb-3">{body.dek}</p>}
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">SOURCES</div>
            <div className="font-display text-2xl text-[#E4DFD3]">{flat.length}</div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">CORE CLAIMS</div>
            <div className="font-display text-2xl text-[#E4DFD3]">{(cm.core_claims || []).length}</div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">CONTRADICTIONS</div>
            <div className={"font-display text-2xl " + (cd.length ? "text-[#D92D20]" : "text-[#5DD4B3]")}>{cd.length}</div>
          </div>
        </div>

        {/* Editorial decision row — primary destination of the case file */}
        <div className="border-t border-[#2D2A26] mt-5 pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              // EDITORIAL DECISION
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
              current:
            </span>
            <span data-testid="editorial-status-current"
                  className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E4DFD3] border border-[#454038] px-2 py-0.5">
              {(draft.editorial_status || (isRunning ? "investigating" : "needs_review")).replace(/_/g, " ")}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => decide("approve")} disabled={isRunning || isPublished} data-testid="decide-approve"
              className="inline-flex items-center gap-2 border border-[#5DD4B3]/40 hover:bg-[#0a1612] text-[#5DD4B3] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <CheckCircle2 size={11} /> Approve
            </button>
            <button onClick={() => decide("deny")} disabled={isRunning || isPublished} data-testid="decide-deny"
              className="inline-flex items-center gap-2 border border-[#D92D20]/40 hover:bg-[#1a0808] text-[#D92D20] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <XCircle size={11} /> Deny
            </button>
            <button onClick={() => decide("undetermined")} disabled={isRunning || isPublished} data-testid="decide-undetermined"
              className="inline-flex items-center gap-2 border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <HelpCircle size={11} /> Undetermined
            </button>
            <button onClick={() => decide("hold")} disabled={isRunning || isPublished} data-testid="decide-hold"
              className="inline-flex items-center gap-2 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <Pause size={11} /> Hold
            </button>
            {(draft.editorial_status && draft.editorial_status !== "needs_review") && (
              <button onClick={() => decide("needs_review")} disabled={isRunning || isPublished} data-testid="decide-needs-review"
                className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                ← Send back to review
              </button>
            )}
          </div>
        </div>

        {/* AI assist row — Fill Gaps / Polish / Lock */}
        <div className="border-t border-[#2D2A26] mt-4 pt-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// AI ASSIST</span>
            {draft.ai_locked && <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5"><Lock size={9} className="inline mr-1" /> LOCKED FROM AI</span>}
            {draft.last_fill_gaps_at && <span className="font-mono text-[9px] text-[#757065]">last fill-gaps: {new Date(draft.last_fill_gaps_at).toLocaleString()}</span>}
            {draft.last_polish_at && <span className="font-mono text-[9px] text-[#757065]">last polish: {new Date(draft.last_polish_at).toLocaleString()}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={fillGaps} disabled={isRunning || isPublished || draft.ai_locked} data-testid="fill-gaps-btn"
              className="inline-flex items-center gap-2 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <Wand2 size={11} /> Fill gaps with AI
            </button>
            <button onClick={polish} disabled={isRunning || isPublished || draft.ai_locked} data-testid="polish-btn"
              className="inline-flex items-center gap-2 border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              <Sparkles size={11} /> Polish dossier
            </button>
            <button onClick={toggleLock} disabled={isRunning || isPublished} data-testid="ai-lock-btn"
              className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
              {draft.ai_locked ? <><Unlock size={11} /> Unlock from AI</> : <><Lock size={11} /> Lock from AI</>}
            </button>
            {draft.job_status && draft.job_status.endsWith("_running") && (
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526] flex items-center gap-1">
                <Loader2 className="animate-spin" size={11} /> {draft.job_status.replace(/_/g," ")}
              </span>
            )}
            {draft.job_status && draft.job_status.endsWith("_error") && (
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">
                <AlertTriangle size={11} className="inline" /> {draft.job_status.replace(/_/g," ")} — {draft.job_error}
              </span>
            )}
          </div>
        </div>

        {/* Publishing controls */}
        <div className="border-t border-[#2D2A26] mt-4 pt-4 flex flex-wrap gap-2 items-center">
          <button onClick={save} disabled={saving || isRunning || isPublished} data-testid="draft-save"
            className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#5DD4B3] text-[#E4DFD3] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            {saving ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save draft
          </button>
          <button onClick={markNeedsResearch} disabled={isRunning || isPublished} data-testid="draft-needs-research"
            className="inline-flex items-center gap-2 border border-[#CBB892]/40 hover:bg-[#1a1408] text-[#CBB892] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Microscope size={11} /> Needs more research
          </button>
          <button onClick={archive} disabled={isRunning || isPublished} data-testid="draft-archive"
            className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#A39E93] text-[#A39E93] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Archive size={11} /> Archive
          </button>
          <HeroControl slug={draft.suggested_slug} opToken={opToken} userToken={userToken} />
          <div className="flex-1" />
          {isPublished && draft.published_topic_id && (
            <>
              <Link to={`/topic/${draft.suggested_slug || ""}`}
                className="inline-flex items-center gap-2 border border-[#5DD4B3] text-[#5DD4B3] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                <CheckCircle2 size={11} /> View public dossier
              </Link>
              <button onClick={pullFromPublic} disabled={pulling} data-testid="draft-pull-public"
                className="inline-flex items-center gap-2 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] disabled:opacity-40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                {pulling ? <Loader2 className="animate-spin" size={11} /> : <XCircle size={11} />} Pull from public
              </button>
            </>
          )}
          {!isPublished && (
            <div className="flex items-center gap-2">
              {!isApproved && (
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065]" data-testid="publish-approval-hint">
                  Approve case file before publishing.
                </span>
              )}
              <button onClick={publish} disabled={publishing || isRunning || !isApproved} data-testid="draft-publish"
                className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
                {publishing ? <Loader2 className="animate-spin" size={11} /> : <Send size={11} />}
                Publish dossier
              </button>
            </div>
          )}
        </div>
        {isRunning && (
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526] mt-3">
            <Loader2 size={11} className="animate-spin inline mr-1" />
            AI research in progress — refresh in a few seconds.
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-[#2D2A26]">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`invtab-${t.key}`}
              className={"px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <>
          <SectionShell icon={FileSearch} title="ORIGINAL SUBMISSION">
            <div className="text-sm text-[#A39E93] leading-relaxed whitespace-pre-wrap">{sub?.rationale}</div>
            <div className="mt-3 font-mono text-[10px] text-[#757065]">
              Submitted by <span className="text-[#A39E93]">{sub?.submitter || "anonymous"}</span> ·
              {" "}{sub?.created_at ? new Date(sub.created_at).toLocaleString() : ""}
            </div>
          </SectionShell>
          <SectionShell icon={FileSearch} title="AI FRAMING">
            <Field label="Title suggestion (used on publish)"
              value={(framing.title_suggestions || [])[0]}
              onChange={(v) => setIn("framing.title_suggestions", [v, ...(framing.title_suggestions || []).slice(1)])}
            />
            <Field label="Category suggestion" value={framing.category_suggestion}
              onChange={(v) => setIn("framing.category_suggestion", v)} />
            <Field label="Summary" multiline rows={3} value={framing.summary}
              onChange={(v) => setIn("framing.summary", v)} />
            <Field label="Why it matters" multiline rows={3} value={framing.why_it_matters}
              onChange={(v) => setIn("framing.why_it_matters", v)} />
            <Field label="Suggested slug" value={draft.suggested_slug}
              onChange={(v) => setIn("suggested_slug", v)} />
          </SectionShell>
          {wm.length > 0 && (
            <SectionShell icon={AlertTriangle} title="WHAT'S MISSING BEFORE PUBLICATION" count={wm.length}>
              <ul className="list-disc list-inside text-sm text-[#CBB892] space-y-1">
                {wm.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </SectionShell>
          )}
          {dc.candidates && dc.candidates.length > 0 && (
            <SectionShell icon={AlertTriangle} title="DUPLICATE / OVERLAP CANDIDATES" count={dc.candidates.length}>
              {dc.candidates.map((c, i) => (
                <div key={i} className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-2 text-sm">
                  <div className="text-[#E4DFD3] font-medium">{c.existing_title}</div>
                  <div className="text-[#A39E93] mt-1">{c.reason}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mt-1">
                    Suggested action: {c.action_hint}
                  </div>
                </div>
              ))}
            </SectionShell>
          )}
        </>
      )}

      {tab === "claims" && (
        <>
          <SectionShell title="CORE CLAIMS" count={(cm.core_claims || []).length}>
            {(cm.core_claims || []).map((c, i) => (
              <div key={i} className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-2 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Tag color={KIND_COLOR[c.kind] || "#A39E93"}>{c.kind}</Tag>
                </div>
                <div className="text-[#E4DFD3]">{c.text}</div>
                {c.note && <div className="text-[#A39E93] text-xs mt-1">{c.note}</div>}
              </div>
            ))}
          </SectionShell>
          <SectionShell title="ESTABLISHED FACTS" count={(cm.established_facts || []).length}>
            <ul className="list-disc list-inside text-sm text-[#E4DFD3] space-y-1">
              {(cm.established_facts || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </SectionShell>
          <SectionShell title="ALLEGED OR DISPUTED" count={(cm.alleged_or_disputed || []).length}>
            <ul className="list-disc list-inside text-sm text-[#CBB892] space-y-1">
              {(cm.alleged_or_disputed || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </SectionShell>
          <SectionShell title="UNSUPPORTED" count={(cm.unsupported || []).length}>
            <ul className="list-disc list-inside text-sm text-[#D92D20] space-y-1">
              {(cm.unsupported || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </SectionShell>
        </>
      )}

      {tab === "timeline" && (
        <SectionShell title="TIMELINE" count={tl.length}>
          <ol className="space-y-3">
            {tl.map((t, i) => (
              <li key={i} className="border-l-2 border-[#D92D20] pl-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">{t.date}</div>
                <div className="text-[#E4DFD3] text-sm">{t.event}</div>
                {Array.isArray(t.actors) && t.actors.length > 0 && (
                  <div className="font-mono text-[10px] text-[#757065] mt-1">actors: {t.actors.join(", ")}</div>
                )}
                {t.source_hint && <div className="font-mono text-[10px] text-[#757065]">source: {t.source_hint}</div>}
              </li>
            ))}
          </ol>
        </SectionShell>
      )}

      {tab === "sources" && (
        <>
          {Object.entries(BUCKET_LABEL).map(([key, label]) => {
            const items = flat.filter((s) => s.bucket === key);
            if (!items.length) return null;
            return (
              <SectionShell key={key} icon={BookOpen} title={label.toUpperCase()} count={items.length}>
                <ul className="space-y-2">
                  {items.map((s, i) => (
                    <li key={i} className="border border-[#2D2A26] bg-[#0F0F0F] p-3 text-sm">
                      <div className="flex items-start gap-2">
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                           className="text-[#E4DFD3] font-medium hover:text-[#D92D20] inline-flex items-center gap-1">
                          {s.title || s.url} <ExternalLink size={10} />
                        </a>
                        {s.uncertain_url && (
                          <Tag color="#E0A526" title="AI is unsure this URL exists — verify before publication">UNCERTAIN URL</Tag>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-[#757065] mt-1">{s.publisher || (s.url || "").replace(/^https?:\/\//, "").split("/")[0]}</div>
                      {s.note && <div className="text-[#A39E93] text-xs mt-1">{s.note}</div>}
                    </li>
                  ))}
                </ul>
              </SectionShell>
            );
          })}
        </>
      )}

      {tab === "evidence" && (
        <>
          <SectionShell title="EVIDENCE MATRIX" count={em.length}>
            {em.map((row, i) => (
              <div key={i} className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Tag color={row.confidence === "high" ? "#5DD4B3" : row.confidence === "low" ? "#D92D20" : "#CBB892"}>
                    {row.confidence}
                  </Tag>
                  <div className="text-[#E4DFD3] text-sm font-medium">{row.claim}</div>
                </div>
                {Array.isArray(row.supports) && row.supports.length > 0 && (
                  <div className="text-xs text-[#A39E93] mb-1"><span className="text-[#5DD4B3] font-mono">SUPPORTS:</span> {row.supports.join("; ")}</div>
                )}
                {Array.isArray(row.contradicts) && row.contradicts.length > 0 && (
                  <div className="text-xs text-[#A39E93] mb-1"><span className="text-[#D92D20] font-mono">CONTRADICTS:</span> {row.contradicts.join("; ")}</div>
                )}
                {Array.isArray(row.missing) && row.missing.length > 0 && (
                  <div className="text-xs text-[#A39E93]"><span className="text-[#CBB892] font-mono">MISSING:</span> {row.missing.join("; ")}</div>
                )}
              </div>
            ))}
          </SectionShell>
          {cd.length > 0 && (
            <SectionShell icon={AlertTriangle} title="CONTRADICTIONS / WEAK POINTS" count={cd.length}>
              {cd.map((c, i) => (
                <div key={i} className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-2 text-sm flex items-start gap-3">
                  <Tag color={c.severity === "high" ? "#D92D20" : c.severity === "medium" ? "#CBB892" : "#A39E93"}>{c.severity}</Tag>
                  <div className="text-[#E4DFD3]">{c.issue}</div>
                </div>
              ))}
            </SectionShell>
          )}
        </>
      )}

      {tab === "draft" && (
        <>
          <SectionShell title="DOSSIER DRAFT (internal — not public until you publish)">
            <Field label="Headline"  value={body.headline}  onChange={(v) => setIn("draft.headline", v)} />
            <Field label="Dek (one-line summary)" value={body.dek} onChange={(v) => setIn("draft.dek", v)} />
            <Field label="Why this case matters" multiline rows={3} value={body.why_it_matters} onChange={(v) => setIn("draft.why_it_matters", v)} />
            <Field label="Background" multiline rows={5} value={body.background} onChange={(v) => setIn("draft.background", v)} />
            <Field label="Central claims" multiline rows={4} value={body.central_claims} onChange={(v) => setIn("draft.central_claims", v)} />
            <Field label="Strongest evidence" multiline rows={4} value={body.strongest_evidence} onChange={(v) => setIn("draft.strongest_evidence", v)} />
            <Field label="Counterevidence & skepticism" multiline rows={4} value={body.counterevidence_and_skepticism} onChange={(v) => setIn("draft.counterevidence_and_skepticism", v)} />
            <Field label="Open questions" multiline rows={3} value={body.open_questions} onChange={(v) => setIn("draft.open_questions", v)} />
            <Field label="Bibliography intro" multiline rows={2} value={body.bibliography_intro} onChange={(v) => setIn("draft.bibliography_intro", v)} />
          </SectionShell>
        </>
      )}

      {tab === "notes" && (
        <>
          <SectionShell title="ADD A NOTE">
            <textarea
              value={notesText} onChange={(e) => setNotesText(e.target.value)}
              rows={3} placeholder="Internal editorial note…"
              data-testid="invest-note-input"
              className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 text-[#E4DFD3] text-sm"
            />
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mt-2">
              Notes are recorded when you save the draft, with your admin email and a timestamp.
            </p>
          </SectionShell>
          <SectionShell title="HISTORY" count={(draft.notes || []).length}>
            {(draft.notes || []).length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">No notes yet.</p>
            ) : (
              <ul className="space-y-2">
                {(draft.notes || []).slice().reverse().map((n, i) => (
                  <li key={i} className="border-l-2 border-[#454038] pl-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                      {n.actor} · {new Date(n.at).toLocaleString()}
                    </div>
                    <div className="text-[#E4DFD3] text-sm whitespace-pre-wrap">{n.text}</div>
                  </li>
                ))}
              </ul>
            )}
          </SectionShell>
          <SectionShell title="SUBMISSION DECISION LOG" count={(sub?.decision_log || []).length}>
            {(sub?.decision_log || []).length === 0 ? (
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065]">No log entries yet.</p>
            ) : (
              <ul className="space-y-2">
                {(sub?.decision_log || []).slice().reverse().map((n, i) => (
                  <li key={i} className="border-l-2 border-[#D92D20]/40 pl-3">
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                      {n.actor} · {n.action} · {new Date(n.at).toLocaleString()}
                    </div>
                    {n.note && <div className="text-[#A39E93] text-sm">{n.note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </SectionShell>
        </>
      )}

      <div className="mt-6 flex justify-end">
        <button onClick={load} className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
    </div>
  );
};

export default AdminInvestigation;
